import {
  addToast,
  Button,
  DateInput,
  Modal,
  Select,
  TextInput,
} from '@octano/global-ui';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { AdmissionScope, Offer, OfferListFormValues, Process } from '../types';
import { getSelectedAdmissionScope } from '../utils';
import { useProcessList } from './ProcessListLoader';
import { updateAdmission } from '../api';

interface Props {
  toggle: (offer?: any) => void;
  offer: Offer;
  process: Process;
}

const DEFAULT_VALUES = {
  startDate: null,
  duration: null,
  enrollmentType: null,
};

export default function OffersListEditModal({ toggle, offer }: Props) {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_ACADEMIC_OFFER);

  const { refresh } = useProcessList();

  const texts = useMemo(() => {
    const prefix = 'offersList.modalForm';
    return {
      title: t(`${prefix}.title`),
      startDate: t(`${prefix}.startDate`),
      duration: t(`${prefix}.duration`),
      enrollmentType: t(`${prefix}.enrollmentType`),
      save: t(`${prefix}.save`),
      close: t(`${prefix}.close`),
    };
  }, [t]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<OfferListFormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const toggleModal = () => {
    reset(DEFAULT_VALUES);
    toggle();
  };

  const handleClose = useCallback(() => {
    reset(DEFAULT_VALUES);
    toggle(undefined);
  }, [reset, toggle]);

  const onSubmit = useCallback(
    async (data: OfferListFormValues) => {
      const { startDate, duration, enrollmentType } = data;

      const response = await updateAdmission({
        admissionId: offer.admissionId,
        data: {
          startDate,
          duration,
          admissionScopes: enrollmentType?.value
            ? (enrollmentType.value.toString().split(',') as AdmissionScope[])
            : null,
        },
      });

      if (response.status === 200) {
        addToast({
          text: t('processList.modalForm.success'),
          color: 'success',
        });
      } else {
        addToast({
          text: t('processList.modalForm.error'),
          color: 'danger',
        });
      }
      refresh();
      handleClose();
    },
    [t, offer.admissionId, refresh, handleClose],
  );

  useEffect(() => {
    reset({
      startDate: offer?.startDate ?? null,
      duration: offer?.duration ?? null,
      enrollmentType: getSelectedAdmissionScope(offer?.admissionScopes ?? []),
    });
  }, [offer, reset]);

  return (
    <Modal isOpen toggle={toggle} size="lg" closeOnBackdrop={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12}>
            <h3 className="fs-22 text-center mb-3">{texts.title}</h3>
          </Col>
          <Col xs={12} md={6} className="mt-3">
            <DateInput
              label={texts.startDate}
              control={control}
              name="startDate"
            />
          </Col>
          <Col xs={12} md={6} className="mt-3">
            <TextInput
              label={texts.duration}
              control={control}
              name="duration"
            />
          </Col>
          <Col xs={12} className="mt-3">
            <Select
              label={texts.enrollmentType}
              control={control}
              name="enrollmentType"
              options={[
                {
                  label: 'Matrícula presencial',
                  value: AdmissionScope.OS,
                },
                {
                  label: 'Matrícula autoatendida (online)',
                  value: AdmissionScope.OL,
                },
                {
                  label: 'Matrícula presencial y autoatendida',
                  value: `${AdmissionScope.OS},${AdmissionScope.OL}`,
                },
              ]}
            />
          </Col>
        </Row>
        <Row className="mt-5">
          <Col xs={6}>
            <Button
              text={texts.close}
              outlined
              onClick={toggleModal}
              fullwidth
              disabled={isSubmitting}
            />
          </Col>
          <Col xs={6}>
            <Button
              text={texts.save}
              type="submit"
              fullwidth
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </form>
    </Modal>
  );
}
